import { useEffect, useRef, useState } from "react";
import { copy } from "../copy";
import { formatBps, formatMoney } from "../format";
import { contestHref, type Pick } from "../feed";
import { estimatePayoutCents, noRivalBacked, parseCredits } from "../payout";
import { fullPageLoad } from "../navigate";
import Icon from "./Icon";

// Places the pick and resolves to null, or to the reason it was refused. A
// refusal with a follow-up (insufficient funds → checkout) shows its reason
// while the follow-up runs; the follow-up resolves to null when it navigated
// away, or to the reason to show instead.
export type Refusal = { reason: string; next: () => Promise<string | null> };
export type PlacePick = (amountCents: number, idempotencyKey: string) => Promise<string | Refusal | null>;

type Props = { pick: Pick; onClose: () => void; onPlace?: PlacePick };

function newKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// On the landing page the slip never writes a stake: Place Pick carries the
// selection to the contest page. On the contest page (onPlace set) it posts
// the pick, with one idempotency key for the life of the slip, so a retry
// after a failure can never debit twice.
// Parents key it by pick, so choosing another producer starts a fresh slip.
// It is not modal: the cards behind it stay live, so another Back swaps the
// selection.
export default function BetSlip({ pick, onClose, onPlace }: Props) {
  const { contest, outcome } = pick;
  const [text, setText] = useState("");
  const [lastValidCents, setLastValidCents] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useRef(newKey()).current;
  const inputRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Focus the amount once on open; Escape closes.
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const enteredCents = parseCredits(text);
  const invalid = text !== "" && enteredCents === null;
  const seed = contest.seed_cents ?? 0;
  const lone = contest.pool_cents > 0 && noRivalBacked(contest.pool_cents, outcome.staked_cents ?? 0, seed);
  const hasPool = contest.pool_cents > 0 && !lone;
  const payoutCents =
    lastValidCents === null
      ? null
      : estimatePayoutCents(contest.pool_cents, contest.take_bps ?? 0, outcome.staked_cents ?? 0, lastValidCents, seed);

  const update = (value: string) => {
    setText(value);
    const cents = parseCredits(value);
    if (cents !== null) setLastValidCents(cents);
  };

  const place = async () => {
    if (enteredCents === null || busy) return;
    if (!onPlace) {
      fullPageLoad(contestHref(contest.slug, outcome.producer_id));
      return;
    }
    setBusy(true);
    setError(null);
    const refused = await onPlace(enteredCents, key);
    if (refused !== null && typeof refused === "object") {
      setError(refused.reason);
      const after = await refused.next();
      if (after === null) return; // leaving for checkout; keep the button disabled
      setError(after);
      setBusy(false);
      return;
    }
    setBusy(false);
    if (refused) setError(refused);
  };

  return (
    <aside
      data-testid="bet-slip"
      role="dialog"
      aria-label={copy.slip.heading}
      className="fixed inset-x-0 bottom-0 z-[60] max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-ground-edge bg-ground-raised p-5 shadow-[0_-24px_60px_rgba(0,0,0,0.65)] motion-safe:animate-slip-up lg:inset-x-auto lg:right-0 lg:top-0 lg:max-h-none lg:w-80 lg:rounded-none lg:border-l lg:border-t-0 lg:p-6 lg:motion-safe:animate-slip-in"
    >
      <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-ground-edge lg:hidden" aria-hidden />
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-chrome-dim">{contest.name}</p>
          <p className="mt-1 truncate font-headline text-2xl uppercase text-chrome">@{outcome.username}</p>
        </div>
        <button
          type="button"
          aria-label={copy.slip.close}
          onClick={onClose}
          className="bf-focusable flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-ground-edge text-xl text-chrome-dim transition-colors hover:text-chrome"
        >
          <Icon name="close" />
        </button>
      </div>

      <div className="mb-5 grid grid-cols-3 gap-2 rounded-lg border border-ground-edge bg-ground p-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-chrome-dim">{copy.slip.odds}</p>
          <p className="bf-tabular font-headline text-lg text-gold">{outcome.american_odds}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-chrome-dim">{copy.slip.share}</p>
          <p className="bf-tabular font-headline text-lg text-chrome">{formatBps(outcome.share_bps)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-chrome-dim">{copy.slip.backers}</p>
          <p className="bf-tabular font-headline text-lg text-chrome">{outcome.backer_count}</p>
        </div>
      </div>

      <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-chrome-dim">{copy.slip.stakeLabel}</p>
      <div className="mb-3 flex gap-2">
        {copy.slip.quickAmounts.map((n) => {
          const active = enteredCents === n * 100;
          return (
            <button
              key={n}
              type="button"
              onClick={() => update(String(n))}
              className={`bf-focusable bf-tabular h-11 flex-1 rounded-md border text-sm font-bold transition-colors ${
                active ? "border-money bg-money/15 text-money" : "border-ground-edge bg-ground text-chrome-dim hover:text-chrome"
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <label className="block">
        <span className="sr-only">{copy.slip.amountLabel}</span>
        <span className="relative block">
          <input
            ref={inputRef}
            data-testid="slip-amount"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={text}
            onChange={(e) => update(e.target.value)}
            aria-invalid={invalid}
            placeholder="0"
            className="bf-tabular h-11 w-full rounded-md border border-ground-edge bg-ground pl-3 pr-20 text-sm font-bold text-chrome placeholder:font-normal placeholder:text-chrome-dim focus:border-money focus:outline-none"
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.16em] text-chrome-dim">
            {copy.slip.amountLabel}
          </span>
        </span>
      </label>
      {invalid && <p className="mt-2 text-xs font-bold text-alert">{copy.slip.invalid}</p>}

      <div className="mt-5 rounded-xl border border-money/25 bg-money/5 p-4">
        {hasPool ? (
          <>
            <p className="text-[10px] uppercase tracking-[0.2em] text-chrome-dim">{copy.slip.payoutLabel}</p>
            {payoutCents !== null && (
              <p
                data-testid="potential-payout"
                aria-live="polite"
                aria-atomic="true"
                className="bf-tabular mt-1 font-headline text-5xl leading-none text-money"
              >
                {formatMoney(payoutCents)}
              </p>
            )}
            <p className="mt-2 text-[11px] leading-relaxed text-chrome-dim">{copy.slip.winLine(outcome.username)}</p>
            <p className="mt-1 text-[11px] leading-relaxed text-chrome-dim">
              {onPlace ? copy.slip.estimateNoteLive : copy.slip.estimateNote}
            </p>
          </>
        ) : lone ? (
          <p data-testid="slip-no-rival" className="text-sm text-chrome-dim">
            {copy.slip.noRival(seed > 0 ? formatMoney(seed) : null)}
          </p>
        ) : (
          <p className="text-sm text-chrome-dim">{copy.slip.noPool}</p>
        )}
        {contest.take_bps !== undefined && (
          <p data-testid="slip-fee" className="mt-2 text-[11px] font-bold leading-relaxed text-chrome">
            {contest.rake_bps !== undefined &&
            contest.producer_bps !== undefined &&
            contest.rake_bps + contest.producer_bps === contest.take_bps
              ? copy.slip.fee(formatBps(contest.rake_bps), formatBps(contest.producer_bps))
              : copy.slip.feeTotal(formatBps(contest.take_bps))}
          </p>
        )}
      </div>

      {error && (
        <p role="alert" data-testid="slip-error" className="mt-4 text-xs font-bold leading-relaxed text-chrome">
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={place}
        disabled={enteredCents === null || busy}
        className="bf-focusable mt-4 h-12 w-full rounded-md bg-money text-sm font-bold uppercase tracking-[0.12em] text-ground transition-colors hover:bg-money-deep disabled:cursor-not-allowed disabled:bg-ground-edge disabled:text-chrome-dim"
      >
        {busy ? copy.slip.placing : copy.slip.place}
      </button>
      <p className="mt-3 text-[11px] leading-relaxed text-chrome-dim">{copy.slip.footnote}</p>
    </aside>
  );
}
